import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { getHighLevelContext } from '@/utils/highlevel';
import toast from 'react-hot-toast';

interface UseEntityMappingsOptions {
  enabled?: boolean;
}

export function useEntityMappings(
  entityType?: 'contact' | 'product' | 'invoice',
  options: UseEntityMappingsOptions = {}
) {
  const { enabled = true } = options;
  const queryClient = useQueryClient();
  const context = getHighLevelContext();
  const locationId = context?.locationId || '';

  // Only enable queries when both enabled option is true AND we have a locationId
  const shouldFetch = enabled && !!locationId;

  // Get entity mappings
  const {
    data: mappingsData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['entity-mappings', locationId, entityType],
    queryFn: () => apiClient.getEntityMappings(locationId, entityType, 50, 0),
    enabled: shouldFetch,
  });

  // Resolve conflict
  const resolveConflictMutation = useMutation({
    mutationFn: ({
      mappingId,
      resolution,
    }: {
      mappingId: string;
      resolution: 'use_highlevel' | 'use_xero';
    }) => apiClient.resolveConflict(mappingId, resolution),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['entity-mappings', locationId, entityType],
      });
      queryClient.invalidateQueries({ queryKey: ['sync-stats', locationId] });
      toast.success('Conflict resolved successfully');
    },
    onError: (error) => {
      toast.error(`Failed to resolve conflict: ${error.message}`);
    },
  });

  const conflicts =
    mappingsData?.mappings?.filter((m) => m.syncStatus === 'conflict') || [];
  const failed =
    mappingsData?.mappings?.filter((m) => m.syncStatus === 'failed') || [];
  const synced =
    mappingsData?.mappings?.filter((m) => m.syncStatus === 'synced') || [];

  return {
    mappings: mappingsData?.mappings || [],
    totalMappings: mappingsData?.total || 0,
    conflicts,
    failed,
    synced,
    isLoading,
    error,
    resolveConflict: resolveConflictMutation.mutate,
    isResolvingConflict: resolveConflictMutation.isPending,
  };
}
